import { Children, ReactNode } from 'react';
import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import {
  StyledComponent,
  StyledDivComponent,
  StyledLIComponent
} from '../types';
import { FontIconSpan } from './FontIcon';
import { Select } from './Form';
import { Icon } from './Icon';
import {
  Input,
  InputContainer,
  IntroSearchBar,
  ResultsContainer,
  ResultType
} from './IntroSearch';
import {
  NavigationLink,
  NavigationList,
  NavigationListItem
} from './Navigation';
import { rgba } from './utils/rgba';

export const NavigationWrapperMobile: StyledDivComponent = styled.div`
  /* position: absolute; */
  background: ${(props) => props.theme.colors.white};
  width: 100%;
  min-height: 100%;
  z-index: 200;
  border-top: 1px solid ${(props) => props.theme.colors.gray};
  /* overflow-y: scroll; */
  pointer-events: all;

  ${IntroSearchBar} {
    position: relative;
    height: auto;
    border-bottom: 1px solid
      ${(props) => props.theme.colors.gray};
    margin-bottom: 0;

    ${InputContainer} {
      padding: 0;
      margin: 0;
      background: transparent !important;

      ${Input} {
        border-bottom: 0;
        font-size: 1.8rem;
        background: ${(props) =>
          props.theme.colors.grayLight};
        padding: 12px 55px 11px 30px;
      }
    }
  }

  ${ResultsContainer} {
    ${ResultType} {
      padding: 6px 30px;
      font-size: 1.8rem;
    }
  }
`;

interface NavigationBGMobileProps {
  show?: boolean;
}

type NavigationBGMobilePropsThemed = ThemeProps<Theme> &
  NavigationBGMobileProps;

export const NavigationBGMobile: StyledDivComponent<
  NavigationBGMobileProps
> = styled.div`
  background: ${(props: NavigationBGMobilePropsThemed) =>
    rgba(props.theme.colors.secondary, 0.5)};
  width: 100%;
  height: 100%;
  z-index: 180;
  display: ${(props: NavigationBGMobilePropsThemed) =>
    props.show ? 'block' : 'none'};
`;

export const NavigationCopyAlertMobile: StyledDivComponent = styled.div`
  width: 100%;
  height: 75px;
  background: ${(props: NavigationBGMobilePropsThemed) =>
    props.theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.8rem;
  position: relative;
  text-align: center;
  margin-top: 0;
  padding: 15px;
  top: 100%;
  animation: sticky-alert-mobile-slide-in-out 4s 1 ease-in-out;

  @keyframes sticky-alert-mobile-slide-in-out {
    from { margin-top: 0; }
    20% { margin-top: -75px; }
    80% { margin-top: -75px; }
    to { margin-top: 0; }
  }
`;


interface NavigationListItemMobileProps {
  open?: boolean;
}

type NavigationListItemMobilePropsThemed = ThemeProps<Theme> &
  NavigationListItemMobileProps & {
    children?: ReactNode;
  };

export const NavigationListItemMobile: StyledLIComponent<
  NavigationListItemMobileProps
> = NavigationListItem.extend`
  display: block;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid
    ${(props: NavigationListItemMobilePropsThemed) => props.theme.colors.gray};

  ${(props: NavigationListItemMobilePropsThemed) =>
    Children.count(props.children) > 1 &&
    css`
      &:before,
      &:after {
        content: '';
        position: absolute;
        right: 0;
        top: 28px;
        background-color: ${props.theme.colors.primary};
        transition: all 0.2s ease-in-out;
      }

      &:before {
        width: 16px;
        height: 2px;
        margin-left: -8px;
        margin-top: -1px;
      }

      &:after {
        width: 2px;
        height: 16px;
        margin-right: 7px;
        margin-top: -8px;
      }
    `}

  ${NavigationLink} {
    margin-right: ${(props: NavigationListItemMobilePropsThemed) =>
      Children.count(props.children) ? '20px' : '0'};
  }

  ${(props: NavigationListItemMobilePropsThemed) =>
    props.open &&
    css`
      &:before {
        transform: rotate(90deg);
        opacity: 0;
      }

      &:after {
        transform: rotate(90deg);
      }
    `}

  ${NavigationLink} {
    display: block;
    font-weight: normal;
    color: ${(props: NavigationListItemMobilePropsThemed) =>
      props.theme.colors.grayDark};
    padding: 15px;

    &:hover {
      color: ${(props: NavigationListItemMobilePropsThemed) =>
        props.theme.colors.primary};
    }

    ${FontIconSpan} {
      padding-left: 30px;
      position: relative;

      &:before {
        position: absolute;
        left: 0;
      }

      &[icon='sendMail'] {
        &:before {
          font-size: 26px;
        }
      }
    }
  }

  //second level
  ${NavigationList} {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.5s linear;

    ${props =>
      props.open &&
      css`
        max-height: 2000px;
      `};
  }

  ${NavigationListItem} {
    &:first-child {
      border-top: 1px solid ${props => props.theme.colors.gray};
    }

    &:last-child {
      border-bottom: 0px;
    }

    ${NavigationLink} {
      &:before {
        font-size: 12px;
        color: ${props => props.theme.colors.primary};
      }
    }
  }
`;

export const NavigationToggleButton = styled.button`
  position: absolute;
  outline: none !important;
  right: -20px;
  top: 0;
  line-height: 0.5;
  padding: 15px;
  max-height: 100%;
  color: transparent;
  border: none;
  margin: 0;
  background: transparent;
  &:after {
    font-size: 3.5rem;
    content: '+';
  }
`;

interface NavigationMobileProps {
  meta?: boolean;
}

type NavigationMobilePropsThemed = ThemeProps<Theme> & NavigationMobileProps;

export const NavigationMobile: StyledComponent<
  NavigationMobileProps
> = styled.nav`
  margin: ${(props: NavigationMobilePropsThemed) =>
    props.meta ? '0' : '0 20px'};
  background: ${(props: NavigationMobilePropsThemed) =>
    props.meta ? props.theme.colors.grayBg : 'none'};

  ${NavigationListItemMobile} {
    ${(props: NavigationMobilePropsThemed) =>
      props.meta &&
      css`
        padding: 0 20px;
      `}

    ${(props: NavigationMobilePropsThemed) =>
      !props.meta &&
      css`
        &:last-child {
          border-bottom: 0;
        }
      `}

    ${Select} {
      position: relative;

      select {
        background: transparent;
        padding-left: 42px;
        font-size: 1.8rem;
        line-height: 1;
        font-weight: 400;
        color: ${(props: NavigationMobilePropsThemed) =>
          props.theme.colors.grayDark};
        border: 0px;
      }

      ${(props: NavigationMobilePropsThemed) =>
        props.meta &&
        css`
          margin: 0 -20px;
          padding: 0 20px;
          background: ${props.theme.colors.grayBg};
        `}

      ${Icon} {
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;

        svg {
          transform: none;
          position: relative;
          right: 0;
          top: 0;
        }
      }
    }
  }
`;

export const LanguageIcon = styled.span`
  left: 35px;
  position: absolute;
  top: 50%;

  ${Icon} {
    width: 20px;
    height: 20px;
    display: block;
    left: 0;

    svg {
      width: 20px;
      height: 20px;
      right: 0;
    }
  }
`;
